import React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import "./index.css";
import "./i18n"; // initialize i18next before React renders
import { ApplicationInsightsService } from "./services/applicationInsights";

// Initialize early App Insights tracking before React starts
// This helps track issues on all platforms, especially mobile
ApplicationInsightsService.initializeEarly();



// Enhanced error boundary for the app
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
    
    // Track error in App Insights if available
    if (window.appInsightsEarly) {
      window.appInsightsEarly.trackException({
        exception: error,
        severityLevel: 3,
        properties: {
          errorInfo: JSON.stringify(errorInfo),
          platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent),
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '\'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif',
          color: 'white'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '500px',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            color: '#333'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</div>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600' }}>App Loading Issue</h2>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: '1.6' }}>
              There was a problem loading the app. This might be due to recent updates or network issues.
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                background: 'linear-gradient(45deg, #0078d4, #106ebe)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,120,212,0.3)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              🔄 Reload App
            </button>
            <details style={{ marginTop: '24px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>Technical Details</summary>
              <pre style={{ 
                background: '#f8f9fa', 
                padding: '12px', 
                borderRadius: '6px', 
                fontSize: '12px',
                marginTop: '8px',
                overflow: 'auto',
                maxHeight: '150px'
              }}>
                {this.state.error?.toString()}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>
);
